/**
 * useCompanyIdentities — Phase 8.LP.LR
 * -----------------------------------
 * Batched, cache-only hook for fetching company identity (logo,
 * name, sector, ...) for one OR many tickers. Powers logo rendering
 * on the landingpage, watchlist, portfolio, analyze loading screen,
 * and any other listing surface that wants a real logo without
 * triggering an FMP call.
 *
 * Contract:
 *   * Accepts an array of tickers. Deduplicated + upper-cased in
 *     flight so callers can pass raw payload arrays.
 *   * Batches into a SINGLE GET /api/companies/identity request per
 *     unique ticker set (respecting the server-side max of 50).
 *   * Returns a `{ ticker -> identity }` map plus a `getIdentity`
 *     helper for consumers that prefer imperative lookups.
 *   * On error, resolves to an empty map — the caller renders
 *     initials fallback. Never throws.
 *   * Never triggers FMP or the LLM (server-side is cache-only).
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const MAX_PER_REQUEST = 50;
const FALLBACK = {
  ticker: "",
  name: null,
  logo_url: null,
  exchange: null,
  exchange_short: null,
  sector: null,
  industry: null,
  country: null,
  currency: null,
  source: "fallback",
};

function normalizeList(tickers) {
  if (!tickers) return [];
  const arr = Array.isArray(tickers) ? tickers : [tickers];
  const out = [];
  const seen = new Set();
  for (const raw of arr) {
    if (raw == null) continue;
    const t = String(raw).trim().toUpperCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

// Module-level cache — dedupes across component instances so the
// same watchlist row + portfolio holding don't re-fetch.
const memoryCache = new Map(); // ticker -> identity
const inflight = new Map(); // ticker set key -> Promise

async function fetchIdentities(tickers) {
  const list = normalizeList(tickers);
  if (list.length === 0) return {};
  // Split into chunks of MAX_PER_REQUEST just in case a caller
  // passes a bigger set.
  const chunks = [];
  for (let i = 0; i < list.length; i += MAX_PER_REQUEST) {
    chunks.push(list.slice(i, i + MAX_PER_REQUEST));
  }
  const merged = {};
  await Promise.all(
    chunks.map(async (chunk) => {
      const key = chunk.join(",");
      let promise = inflight.get(key);
      if (!promise) {
        promise = api
          .get("/companies/identity", { params: { tickers: key } })
          .then((r) => r?.data?.identities || {})
          .catch(() => ({}));
        inflight.set(key, promise);
        // Clean up once the promise settles so a subsequent call
        // with the same set does re-fetch (identity does change on
        // corporate actions).
        promise.finally(() => inflight.delete(key));
      }
      const data = await promise;
      Object.assign(merged, data);
    })
  );
  // Cache successful lookups.
  for (const [tk, ident] of Object.entries(merged)) {
    memoryCache.set(tk, ident);
  }
  return merged;
}

/**
 * Hook variant.
 *
 * @param {string[]|string} tickers - list (or single) of tickers.
 * @returns {{
 *   identities: Object<string, Object>,
 *   loading: boolean,
 *   getIdentity: (ticker: string) => Object,
 * }}
 */
export function useCompanyIdentities(tickers) {
  const list = useMemo(() => normalizeList(tickers), [tickers]);
  const listKey = list.join(",");

  const [identities, setIdentities] = useState(() => {
    // Seed with anything already in the module cache so first render
    // shows real logos immediately.
    const initial = {};
    for (const t of list) {
      const cached = memoryCache.get(t);
      if (cached) initial[t] = cached;
    }
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    if (list.length === 0) {
      setIdentities({});
      return;
    }
    // Determine which tickers still need fetching.
    const missing = list.filter((t) => !memoryCache.has(t));
    if (missing.length === 0) {
      const map = {};
      for (const t of list) map[t] = memoryCache.get(t) || { ...FALLBACK, ticker: t };
      setIdentities(map);
      return;
    }
    setLoading(true);
    fetchIdentities(missing)
      .then(() => {
        if (!mounted.current) return;
        const map = {};
        for (const t of list) map[t] = memoryCache.get(t) || { ...FALLBACK, ticker: t };
        setIdentities(map);
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    // `list` is intentionally omitted: `listKey` is its canonical
    // stable projection (sorted, deduped, uppercased tickers joined
    // by "|"), so it changes iff the identity we need to fetch
    // changes.  Adding `list` would rerun the fetch on every render
    // because array identity is not preserved by the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  const getIdentity = useCallback(
    (ticker) => {
      const t = String(ticker || "").toUpperCase();
      return identities[t] || memoryCache.get(t) || { ...FALLBACK, ticker: t };
    },
    [identities]
  );

  return { identities, loading, getIdentity };
}

/**
 * Imperative helper — for surfaces that don't want a hook.
 * Returns a promise that resolves to a `{ticker -> identity}` map.
 */
export async function prefetchCompanyIdentities(tickers) {
  return fetchIdentities(tickers);
}

/**
 * Utility — read from the module cache without triggering a fetch.
 * Returns `null` when the ticker has not yet been fetched.
 */
export function readCachedIdentity(ticker) {
  if (!ticker) return null;
  const t = String(ticker).toUpperCase();
  return memoryCache.get(t) || null;
}

// Exposed for tests to reset the module cache between runs.
export function __resetIdentityCacheForTests__() {
  memoryCache.clear();
  inflight.clear();
}

export default useCompanyIdentities;
